import React, { useState } from 'react';
import { ArrowRight, ShieldCheck, KeyRound, Globe, Calendar, Compass } from 'lucide-react';
import { AuditInstance, KeywordResearchProject, OnPageSeoProject, SEOModule } from '../../types';
import { ScoringEngine } from '../../services/scoringEngine';
import { ReportViewer } from './ReportViewer';
import { KeywordResearchWorkspace } from '../KeywordResearch/KeywordResearchWorkspace';
import { OnPageSeoWorkspace } from '../OnPageSeo/OnPageSeoWorkspace';

interface FinalReportsCenterProps {
  audits: AuditInstance[];
  kwProjects: KeywordResearchProject[];
  onPageProjects?: OnPageSeoProject[];
  module: SEOModule;
  activeAudit: AuditInstance | null;
  onSelectAuditReport: (audit: AuditInstance) => void;
  onSaveAudit: (audit: AuditInstance) => void;
  onSaveKwProject: (project: KeywordResearchProject) => void;
  onSaveOnPageProject?: (project: OnPageSeoProject) => void;
}

export const FinalReportsCenter: React.FC<FinalReportsCenterProps> = ({
  audits,
  kwProjects,
  onPageProjects = [],
  module,
  onSelectAuditReport,
  onSaveAudit,
  onSaveKwProject,
  onSaveOnPageProject,
}) => {
  // Always default to null so clicking Final Reports directly opens the main Final Reports Center list!
  const [selectedAuditForReport, setSelectedAuditForReport] = useState<AuditInstance | null>(null);
  const [selectedKwProjectForReport, setSelectedKwProjectForReport] = useState<KeywordResearchProject | null>(null);
  const [selectedOnPageForReport, setSelectedOnPageForReport] = useState<OnPageSeoProject | null>(null);

  // If viewing a specific audit report
  if (selectedAuditForReport) {
    return (
      <div>
        <div style={{ background: '#090e1a', borderBottom: '1px solid #1e293b', padding: '12px 28px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <button
            onClick={() => setSelectedAuditForReport(null)}
            style={{
              background: 'rgba(30, 41, 59, 0.6)',
              border: '1px solid #1e293b',
              color: '#38bdf8',
              padding: '6px 14px',
              borderRadius: '8px',
              fontSize: '12px',
              fontWeight: 700,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            ← Back to Final Reports Center
          </button>
          <span style={{ fontSize: '13px', color: '#94a3b8', fontWeight: 600 }}>
            Viewing Audit Report: <strong style={{ color: '#ffffff' }}>{selectedAuditForReport.clientName}</strong>
          </span>
        </div>

        <ReportViewer
          audit={selectedAuditForReport}
          module={module}
          onNavigateWorkspace={() => setSelectedAuditForReport(null)}
          onSaveAudit={onSaveAudit}
        />
      </div>
    );
  }

  // If viewing a specific keyword research report
  if (selectedKwProjectForReport) {
    return (
      <div>
        <div style={{ background: '#090e1a', borderBottom: '1px solid #1e293b', padding: '12px 28px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <button
            onClick={() => setSelectedKwProjectForReport(null)}
            style={{
              background: 'rgba(30, 41, 59, 0.6)',
              border: '1px solid #1e293b',
              color: '#38bdf8',
              padding: '6px 14px',
              borderRadius: '8px',
              fontSize: '12px',
              fontWeight: 700,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            ← Back to Final Reports Center
          </button>
          <span style={{ fontSize: '13px', color: '#94a3b8', fontWeight: 600 }}>
            Viewing Keyword Report: <strong style={{ color: '#ffffff' }}>{selectedKwProjectForReport.clientName}</strong>
          </span>
        </div>

        <KeywordResearchWorkspace
          project={selectedKwProjectForReport}
          audits={audits}
          onSaveProject={onSaveKwProject}
          onBackToDashboard={() => setSelectedKwProjectForReport(null)}
        />
      </div>
    );
  }

  // If viewing a specific on-page SEO report
  if (selectedOnPageForReport) {
    return (
      <div>
        <div style={{ background: '#090e1a', borderBottom: '1px solid #1e293b', padding: '12px 28px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <button
            onClick={() => setSelectedOnPageForReport(null)}
            style={{
              background: 'rgba(30, 41, 59, 0.6)',
              border: '1px solid #1e293b',
              color: '#38bdf8',
              padding: '6px 14px',
              borderRadius: '8px',
              fontSize: '12px',
              fontWeight: 700,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            ← Back to Final Reports Center
          </button>
          <span style={{ fontSize: '13px', color: '#94a3b8', fontWeight: 600 }}>
            Viewing On-Page Report: <strong style={{ color: '#ffffff' }}>{selectedOnPageForReport.clientName}</strong>
          </span>
        </div>

        <OnPageSeoWorkspace
          project={selectedOnPageForReport}
          audits={audits}
          kwProjects={kwProjects}
          onSaveProject={onSaveOnPageProject || (() => {})}
          onBackToDashboard={() => setSelectedOnPageForReport(null)}
        />
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '32px 24px 80px 24px' }}>
      
      {/* Header Banner */}
      <div style={{ marginBottom: '32px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
          <span style={{ fontSize: '11px', fontWeight: 800, color: '#38bdf8', textTransform: 'uppercase', letterSpacing: '1px' }}>
            ZYNEX NINJA ENTERPRISE REPORTS
          </span>
        </div>
        <h1 style={{ fontSize: '28px', color: '#ffffff', fontWeight: 800, margin: '0 0 8px 0', letterSpacing: '-0.02em' }}>
          Final Client Reports Center
        </h1>
        <p style={{ color: '#94a3b8', fontSize: '14px', margin: 0, maxWidth: '640px' }}>
          Access, customize, and export executive PDF reports for all technical website audits and keyword research projects.
        </p>
      </div>

      {/* SECTION 1: TECHNICAL AUDIT REPORTS LIST */}
      <div style={{ marginBottom: '40px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <ShieldCheck size={18} color="#10b981" />
            <h2 style={{ fontSize: '18px', color: '#ffffff', fontWeight: 700, margin: 0 }}>
              Technical Audit Reports ({audits.length})
            </h2>
          </div>
        </div>

        {audits.length === 0 ? (
          <div style={{ background: '#090e1a', border: '1px solid #1e293b', borderRadius: '12px', padding: '32px', textAlign: 'center', color: '#64748b' }}>
            No technical audit reports available. Complete an audit to generate client reports.
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px' }}>
            {audits.map((audit) => {
              const scores = ScoringEngine.calculateScores(audit, module);

              return (
                <div
                  key={audit.id}
                  onClick={() => {
                    onSelectAuditReport(audit);
                    setSelectedAuditForReport(audit);
                  }}
                  style={{
                    background: '#090e1a',
                    border: '1px solid #1e293b',
                    borderRadius: '12px',
                    padding: '20px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                  }}
                >
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                      <span style={{ fontSize: '11px', fontWeight: 700, color: '#10b981', background: 'rgba(16, 185, 129, 0.15)', padding: '2px 8px', borderRadius: '4px' }}>
                        TECHNICAL AUDIT REPORT
                      </span>
                      <span style={{ fontSize: '13px', fontWeight: 800, color: '#ffffff', fontFamily: 'var(--font-mono)' }}>
                        {scores.overallScore}% Score
                      </span>
                    </div>

                    <h3 style={{ fontSize: '16px', color: '#ffffff', fontWeight: 700, margin: '0 0 6px 0' }}>
                      {audit.clientName}
                    </h3>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#94a3b8', marginBottom: '12px' }}>
                      <Globe size={13} color="#64748b" />
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {audit.websiteUrl}
                      </span>
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: '12px', borderTop: '1px solid #1e293b' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: '#64748b' }}>
                      <Calendar size={12} />
                      <span>{new Date(audit.updatedAt).toLocaleDateString()}</span>
                    </div>
                    <span style={{ fontSize: '12px', fontWeight: 700, color: '#38bdf8', display: 'flex', alignItems: 'center', gap: '4px' }}>
                      View Report <ArrowRight size={14} />
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* SECTION 2: KEYWORD RESEARCH REPORTS LIST */}
      <div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <KeyRound size={18} color="#38bdf8" />
            <h2 style={{ fontSize: '18px', color: '#ffffff', fontWeight: 700, margin: 0 }}>
              Keyword Research Reports ({kwProjects.length})
            </h2>
          </div>
        </div>

        {kwProjects.length === 0 ? (
          <div style={{ background: '#090e1a', border: '1px solid #1e293b', borderRadius: '12px', padding: '32px', textAlign: 'center', color: '#64748b' }}>
            No keyword research reports available. Create a keyword project to generate research reports.
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px' }}>
            {kwProjects.map((proj) => {
              const kwCount = proj.masterKeywords?.length || proj.seedKeywords?.length || 0;

              return (
                <div
                  key={proj.id}
                  onClick={() => setSelectedKwProjectForReport(proj)}
                  style={{
                    background: '#090e1a',
                    border: '1px solid #1e293b',
                    borderRadius: '12px',
                    padding: '20px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                  }}
                >
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                      <span style={{ fontSize: '11px', fontWeight: 700, color: '#38bdf8', background: 'rgba(2, 132, 199, 0.15)', padding: '2px 8px', borderRadius: '4px' }}>
                        KEYWORD REPORT
                      </span>
                      <span style={{ fontSize: '12px', fontWeight: 700, color: '#94a3b8', fontFamily: 'var(--font-mono)' }}>
                        {kwCount} Keywords
                      </span>
                    </div>

                    <h3 style={{ fontSize: '16px', color: '#ffffff', fontWeight: 700, margin: '0 0 6px 0' }}>
                      {proj.clientName}
                    </h3>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#94a3b8', marginBottom: '12px' }}>
                      <Globe size={13} color="#64748b" />
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {proj.websiteUrl}
                      </span>
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: '12px', borderTop: '1px solid #1e293b' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: '#64748b' }}>
                      <Calendar size={12} />
                      <span>{new Date(proj.updatedAt).toLocaleDateString()}</span>
                    </div>
                    <span style={{ fontSize: '12px', fontWeight: 700, color: '#38bdf8', display: 'flex', alignItems: 'center', gap: '4px' }}>
                      View Report <ArrowRight size={14} />
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* SECTION 3: ON-PAGE SEO IMPLEMENTATION REPORTS LIST */}
      <div style={{ marginTop: '40px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Compass size={18} color="#10b981" />
            <h2 style={{ fontSize: '18px', color: '#ffffff', fontWeight: 700, margin: 0 }}>
              On-Page SEO Implementation Reports ({onPageProjects.length})
            </h2>
          </div>
        </div>

        {onPageProjects.length === 0 ? (
          <div style={{ background: '#090e1a', border: '1px solid #1e293b', borderRadius: '12px', padding: '32px', textAlign: 'center', color: '#64748b' }}>
            No On-Page SEO implementation plans available. Create a plan to review walkthrough checkpoints and generate reports.
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px' }}>
            {onPageProjects.map((proj) => {
              const checklistItems = Object.values(proj.checklist || {});
              const reviewedCount = checklistItems.filter(i => i.isReviewed).length;
              const totalCount = checklistItems.length;
              const pct = totalCount > 0 ? Math.round((reviewedCount / totalCount) * 100) : 0;

              return (
                <div
                  key={proj.id}
                  onClick={() => setSelectedOnPageForReport(proj)}
                  style={{
                    background: '#090e1a',
                    border: '1px solid #1e293b',
                    borderRadius: '12px',
                    padding: '20px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                  }}
                >
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                      <span style={{ fontSize: '11px', fontWeight: 700, color: '#10b981', background: 'rgba(16, 185, 129, 0.15)', padding: '2px 8px', borderRadius: '4px' }}>
                        ON-PAGE WALKTHROUGH
                      </span>
                      <span style={{ fontSize: '12px', fontWeight: 700, color: pct === 100 ? '#10b981' : '#38bdf8', fontFamily: 'var(--font-mono)' }}>
                        {reviewedCount}/{totalCount} Reviewed ({pct}%)
                      </span>
                    </div>

                    <h3 style={{ fontSize: '16px', color: '#ffffff', fontWeight: 700, margin: '0 0 6px 0' }}>
                      {proj.clientName}
                    </h3>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#94a3b8', marginBottom: '12px' }}>
                      <Globe size={13} color="#64748b" />
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {proj.websiteUrl}
                      </span>
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: '12px', borderTop: '1px solid #1e293b' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: '#64748b' }}>
                      <Calendar size={12} />
                      <span>{new Date(proj.updatedAt).toLocaleDateString()}</span>
                    </div>
                    <span style={{ fontSize: '12px', fontWeight: 700, color: '#10b981', display: 'flex', alignItems: 'center', gap: '4px' }}>
                      View Report <ArrowRight size={14} />
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
